/**
 * InsertNodeControl — insert a new node (S6.2 F1).
 *
 * Collapsed: a "+ 插入节点" affordance. Expanded: a node-type select + a
 * plain-text input. Insert appends an `insert_node` op at the caller's
 * clean-children index.
 */
import { useEffect, useRef, useState } from 'react';

export type InsertNodeType = 'paragraph' | 'clauseHeading' | 'listItem';

const TYPE_LABELS: Record<InsertNodeType, string> = {
  paragraph: '段落',
  clauseHeading: '条款标题',
  listItem: '列表项',
};

interface InsertNodeControlProps {
  busy: boolean;
  onInsert: (text: string, type: InsertNodeType) => void;
  label?: string;
}

export function InsertNodeControl({
  busy,
  onInsert,
  label = '插入节点',
}: InsertNodeControlProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [type, setType] = useState<InsertNodeType>('paragraph');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const reset = () => {
    setText('');
    setType('paragraph');
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-btn-sm items-center gap-1 rounded-sm border border-dashed border-ink-300 bg-ink-50 px-2 text-xs text-ink-500 hover:border-brand-400 hover:bg-brand-50 hover:text-brand-700"
      >
        <span aria-hidden="true">＋</span>
        {label}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border border-brand-200 bg-brand-50 p-3">
      <div className="flex items-center gap-2">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as InsertNodeType)}
          className="h-btn-sm rounded-sm border border-ink-200 bg-ink-0 px-2 text-xs text-ink-700"
          aria-label="新节点类型"
        >
          {(Object.keys(TYPE_LABELS) as InsertNodeType[]).map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              reset();
            } else if (e.key === 'Enter' && text.trim() && !busy) {
              e.preventDefault();
              onInsert(text.trim(), type);
              reset();
            }
          }}
          placeholder="新节点文字…"
          className="min-w-0 flex-1 rounded-sm border border-ink-200 bg-ink-0 px-2 py-1 font-serif text-md text-ink-900 outline-none focus:ring-2 focus:ring-brand-200"
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            if (text.trim()) {
              onInsert(text.trim(), type);
              reset();
            }
          }}
          disabled={busy || !text.trim()}
          className="inline-flex h-btn-sm items-center rounded-sm bg-brand-600 px-3 text-xs font-medium text-ink-0 hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          插入
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={busy}
          className="inline-flex h-btn-sm items-center rounded-sm border border-ink-200 bg-ink-0 px-3 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900 disabled:opacity-40"
        >
          取消
        </button>
      </div>
    </div>
  );
}

export default InsertNodeControl;
